import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@crossnokaye/ux-common';

type ScaledAnimaticProps = {
  scaleX?: number;
  scaleY?: number;
  children?: React.ReactNode;
  className?: string;
};

export const ScaledAnimatic = ({
  scaleX,
  scaleY,
  className,
  children,
}: ScaledAnimaticProps): React.ReactNode => {
  const animaticRef = useRef<HTMLDivElement>(null);
  const [calculatedScale, setCalculatedScale] = useState<number>(1);

  useEffect(() => {
    // If custom scale factors are provided, skip auto-calculation
    if (scaleX !== undefined || scaleY !== undefined) {
      return;
    }

    if (animaticRef.current) {
      // Wait for the animatic to render, then measure it for auto-scaling
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          const { width: nativeWidth, height: nativeHeight } = entries[0].contentRect;

          if (nativeWidth > 0 && nativeHeight > 0) {
            // Calculate scale factor to make the largest dimension 180px
            const maxDimension = Math.max(nativeWidth, nativeHeight);
            const targetSize = 180;
            const autoScale = targetSize / maxDimension;

            setCalculatedScale(autoScale);
          }
        }
      });

      resizeObserver.observe(animaticRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [scaleX, scaleY]);

  // Determine which scale factors to use
  const finalScaleX = scaleX ?? calculatedScale;
  const finalScaleY = scaleY ?? calculatedScale;

  // Create transform string - use scaleX/scaleY for different X/Y scaling, or scale() for uniform
  const transform =
    scaleX !== undefined || scaleY !== undefined
      ? `scaleX(${finalScaleX}) scaleY(${finalScaleY})`
      : `scale(${calculatedScale})`;

  return (
    <div className={cn('flex justify-center items-center', className)}>
      <div
        ref={animaticRef}
        style={{
          transform,
          transformOrigin: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
};
